<template>
  <div class="kds" :class="{'kds-fullscreen': isFullscreen}">
    <!-- Header -->
    <div class="kds-header">
      <div class="kds-header-left">
        <button v-if="!isFullscreen" class="kds-back" @click="$router.push('/dashboard')">←</button>
        <h3 class="kds-title">🍽️ Kitchen Display</h3>
        <span class="kds-live" :class="connected ? 'kds-live-on' : 'kds-live-off'">{{ connected ? '● LIVE' : '○ OFFLINE' }}</span>
      </div>
      <div class="kds-header-mid">
        <select v-model="filterStation" class="kds-select">
          <option value="">All Stations</option>
          <option v-for="s in stations" :key="s.id" :value="s.id">{{ s.name }} ({{ (s.pending_count||0) + (s.preparing_count||0) }})</option>
        </select>
        <select v-model="filterStatus" class="kds-select">
          <option value="active">Active Orders</option>
          <option value="new">🔴 New</option>
          <option value="preparing">🟠 Preparing</option>
          <option value="ready">🟢 Ready</option>
          <option value="collected">✅ Collected</option>
          <option value="">All</option>
        </select>
      </div>
      <div class="kds-header-right">
        <button class="kds-icon-btn" @click="soundEnabled=!soundEnabled" :title="soundEnabled ? 'Mute' : 'Unmute'">{{ soundEnabled ? '🔔' : '🔕' }}</button>
        <button class="kds-icon-btn" @click="toggleFullscreen">{{ isFullscreen ? '⊡' : '⊞' }}</button>
        <button class="kds-icon-btn" @click="showSettings=!showSettings">⚙️</button>
      </div>
    </div>

    <!-- Stats Bar -->
    <div class="kds-stats">
      <div class="kds-stat kds-stat-red"><span class="kds-stat-num">{{ stats.new_count || 0 }}</span><span class="kds-stat-label">New</span></div>
      <div class="kds-stat kds-stat-orange"><span class="kds-stat-num">{{ stats.preparing_count || 0 }}</span><span class="kds-stat-label">Preparing</span></div>
      <div class="kds-stat kds-stat-green"><span class="kds-stat-num">{{ stats.ready_count || 0 }}</span><span class="kds-stat-label">Ready</span></div>
      <div class="kds-stat kds-stat-blue"><span class="kds-stat-num">{{ stats.collected_count || 0 }}</span><span class="kds-stat-label">Done</span></div>
      <div class="kds-stat kds-stat-muted"><span class="kds-stat-num">{{ stats.total_today || 0 }}</span><span class="kds-stat-label">Today</span></div>
      <div class="kds-stat kds-stat-muted" v-if="stats.avg_prep_seconds > 0"><span class="kds-stat-num">{{ Math.round(stats.avg_prep_seconds / 60) }}m</span><span class="kds-stat-label">Avg Prep</span></div>
    </div>

    <!-- Order Grid -->
    <div class="kds-grid" v-if="filteredOrders.length > 0">
      <div v-for="order in filteredOrders" :key="order.id" class="kds-card" :class="'kds-card-' + order.status">
        <div class="kds-card-header">
          <span class="kds-order-num">#{{ order.order_number?.split('-').pop() || order.id }}</span>
          <span class="kds-order-badge" :class="'kds-badge-' + order.status">{{ statusLabel(order.status) }}</span>
          <span v-if="order.priority === 'rush'" class="kds-rush">🔥 RUSH</span>
          <span v-if="order.priority === 'vip'" class="kds-rush kds-vip">⭐ VIP</span>
        </div>
        <div class="kds-card-customer">
          <strong>{{ order.student_name || order.customer_name || 'Walk-in' }}</strong>
          <span v-if="order.table_number" class="kds-table">Table {{ order.table_number }}</span>
        </div>
        <div class="kds-card-items">
          <div v-for="item in order.items" :key="item.id" class="kds-item">
            <span class="kds-item-qty">{{ item.quantity }}×</span>
            <span class="kds-item-name">{{ item.name }}</span>
            <span v-if="item.notes" class="kds-item-note">📝 {{ item.notes }}</span>
          </div>
        </div>
        <div v-if="order.notes" class="kds-card-notes">📝 {{ order.notes }}</div>
        <div class="kds-card-meta">
          <span>{{ order.order_type === 'takeaway' ? '📦 Take Away' : order.order_type === 'delivery' ? '🚗 Delivery' : '🍽️ Dine In' }}</span>
          <span>{{ timeAgo(order.created_at) }}</span>
          <span v-if="order.station_name">📍 {{ order.station_name }}</span>
        </div>
        <div class="kds-card-actions">
          <button v-if="order.status === 'new'" class="kds-btn kds-btn-orange" @click="updateStatus(order, 'preparing')">👨‍🍳 Start Preparing</button>
          <button v-if="order.status === 'preparing'" class="kds-btn kds-btn-green" @click="updateStatus(order, 'ready')">✅ Ready</button>
          <button v-if="order.status === 'ready'" class="kds-btn kds-btn-blue" @click="updateStatus(order, 'collected')">🤝 Collected</button>
          <button v-if="order.status !== 'collected' && order.status !== 'cancelled'" class="kds-btn kds-btn-ghost" @click="cancelOrder(order)">✕</button>
          <button v-if="order.status === 'preparing'" class="kds-btn kds-btn-ghost" @click="updateStatus(order, 'new')">↩ Back</button>
        </div>
        <div v-if="order.status === 'preparing'" class="kds-timer">⏱️ {{ elapsedTime(order.prepared_at || order.created_at) }}</div>
      </div>
    </div>
    <div v-else class="kds-empty">
      <div class="kds-empty-icon">🍽️</div>
      <p>No {{ filterStatus === 'active' ? 'active' : filterStatus }} orders</p>
      <p class="kds-muted">Orders will appear here when placed from POS</p>
    </div>

    <!-- Settings Panel -->
    <div v-if="showSettings" class="kds-overlay" @click="showSettings=false">
      <div class="kds-panel" @click.stop>
        <button class="kds-x" @click="showSettings=false">✕</button>
        <h4>⚙️ Kitchen Settings</h4>

        <div class="kds-section">
          <h6>📍 Stations</h6>
          <div v-for="s in stations" :key="s.id" class="kds-station-row">
            <span>{{ s.name }} ({{ s.type }})</span>
            <button class="kds-btn-sm kds-btn-ghost" @click="deleteStation(s)">🗑️</button>
          </div>
          <div class="kds-add-row">
            <input v-model="newStation.name" placeholder="Station name" class="kds-inp" />
            <select v-model="newStation.type" class="kds-select-sm">
              <option value="kitchen">Kitchen</option>
              <option value="tuckshop">Tuckshop</option>
              <option value="beverage">Beverage</option>
              <option value="grill">Grill</option>
              <option value="bakery">Bakery</option>
              <option value="general">General</option>
            </select>
            <button class="kds-btn-sm kds-btn-green" @click="addStation">+ Add</button>
          </div>
        </div>

        <div class="kds-section">
          <h6>🔔 Notifications</h6>
          <label class="kds-toggle"><input type="checkbox" v-model="soundEnabled" /><span>Sound alerts for new orders</span></label>
          <label class="kds-toggle"><input type="checkbox" v-model="kSettings.notifySms" /><span>SMS when order ready</span></label>
          <label class="kds-toggle"><input type="checkbox" v-model="kSettings.notifyEmail" /><span>Email when order ready</span></label>
        </div>

        <div class="kds-section">
          <h6>🔧 Service Mode</h6>
          <select v-model="kSettings.serviceMode" class="kds-inp">
            <option value="made_to_order">Made to Order (kitchen prepares)</option>
            <option value="counter">Counter Service (already ready)</option>
            <option value="both">Both (cashier chooses per order)</option>
          </select>
        </div>

        <div class="kds-section">
          <h6>⏱️ Auto-collect</h6>
          <div class="kds-muted" style="font-size:12px;margin-bottom:6px;">Auto-mark "Ready" orders as "Collected" after X minutes (0 = disabled)</div>
          <input type="number" v-model.number="kSettings.autoCollectMinutes" placeholder="0" class="kds-inp" style="width:100px" />
        </div>

        <div class="kds-section">
          <h6>🔄 Refresh Interval</h6>
          <select v-model.number="refreshInterval" class="kds-inp" @change="restartPolling">
            <option :value="3">3 seconds</option>
            <option :value="5">5 seconds</option>
            <option :value="10">10 seconds</option>
            <option :value="15">15 seconds</option>
          </select>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
/* eslint-disable */
import { executeJson, simitrack } from '@/api';
export default {
  name: 'KitchenDisplay',
  data() { return {
    orders: [], stations: [], stats: {},
    filterStation: '', filterStatus: 'active',
    showSettings: false, isFullscreen: false, soundEnabled: true, connected: true,
    refreshInterval: 5, pollTimer: null, lastOrderCount: 0,
    newStation: { name: '', type: 'kitchen' },
    kSettings: { notifySms: false, notifyEmail: false, serviceMode: 'made_to_order', autoCollectMinutes: 0 },
    nowTick: Date.now(),
  }; },
  computed: {
    filteredOrders() {
      let list = this.orders;
      if (this.filterStation) list = list.filter(o => o.station_id == this.filterStation);
      if (this.filterStatus === 'active') list = list.filter(o => ['new','preparing','ready'].includes(o.status));
      else if (this.filterStatus) list = list.filter(o => o.status === this.filterStatus);
      return list;
    }
  },
  mounted() {
    this.loadAll();
    this.startPolling();
    // Tick every second for live timers
    setInterval(() => { this.nowTick = Date.now(); }, 1000);
  },
  beforeDestroy() { clearInterval(this.pollTimer); },
  methods: {
    async loadAll() { await Promise.all([this.loadOrders(), this.loadStations(), this.loadStats()]); },
    async loadOrders() {
      try {
        const r = await executeJson({ function: 'getKitchenOrders', status: this.filterStatus || '', stationId: this.filterStation }, simitrack);
        if (r.data.success) {
          const prev = this.orders.length;
          this.orders = r.data.data || [];
          this.connected = true;
          // Sound alert for new orders
          const newCount = this.orders.filter(o => o.status === 'new').length;
          if (newCount > this.lastOrderCount && this.soundEnabled && this.lastOrderCount > 0) this.playAlert();
          this.lastOrderCount = newCount;
        }
      } catch (e) { this.connected = false; }
    },
    async loadStations() {
      try { const r = await executeJson({ function: 'getKitchenStations' }, simitrack); if (r.data.success) this.stations = r.data.data || []; } catch (e) {}
    },
    async loadStats() {
      try { const r = await executeJson({ function: 'getKitchenStats' }, simitrack); if (r.data.success) this.stats = r.data.data || {}; } catch (e) {}
    },
    async updateStatus(order, newStatus) {
      try {
        const r = await executeJson({ function: 'updateOrderStatus', orderId: order.id, status: newStatus }, simitrack);
        if (r.data.success) { order.status = newStatus; this.loadAll(); }
        else alert(r.data.message || 'Failed');
      } catch (e) { alert('Connection error'); }
    },
    async cancelOrder(order) {
      const reason = prompt('Cancel reason (optional):');
      if (reason === null) return;
      try {
        const r = await executeJson({ function: 'updateOrderStatus', orderId: order.id, status: 'cancelled', reason }, simitrack);
        if (r.data.success) { order.status = 'cancelled'; this.loadAll(); }
      } catch (e) { alert('Error'); }
    },
    async addStation() {
      if (!this.newStation.name.trim()) return;
      try {
        await executeJson({ function: 'manageKitchenStation', action: 'add', name: this.newStation.name, type: this.newStation.type }, simitrack);
        this.newStation.name = ''; this.loadStations();
      } catch (e) {}
    },
    async deleteStation(s) {
      if (!confirm(`Delete station "${s.name}"?`)) return;
      try { await executeJson({ function: 'manageKitchenStation', action: 'delete', stationId: s.id }, simitrack); this.loadStations(); } catch (e) {}
    },
    startPolling() { this.pollTimer = setInterval(() => this.loadAll(), this.refreshInterval * 1000); },
    restartPolling() { clearInterval(this.pollTimer); this.startPolling(); },
    statusLabel(s) { return { new: '🔴 NEW', preparing: '🟠 PREPARING', ready: '🟢 READY', collected: '✅ COLLECTED', cancelled: '❌ CANCELLED' }[s] || s; },
    timeAgo(dt) {
      if (!dt) return '';
      const diff = Math.floor((this.nowTick - new Date(dt).getTime()) / 1000);
      if (diff < 60) return `${diff}s ago`;
      if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
      return `${Math.floor(diff / 3600)}h ago`;
    },
    elapsedTime(dt) {
      if (!dt) return '0:00';
      const diff = Math.floor((this.nowTick - new Date(dt).getTime()) / 1000);
      const m = Math.floor(diff / 60); const s = diff % 60;
      return `${m}:${String(s).padStart(2, '0')}`;
    },
    playAlert() {
      try {
        const ctx = new (window.AudioContext || window.webkitAudioContext)();
        [660, 880, 660].forEach((freq, i) => {
          const osc = ctx.createOscillator(); const gain = ctx.createGain();
          osc.connect(gain); gain.connect(ctx.destination);
          osc.frequency.value = freq; osc.type = 'sine';
          gain.gain.value = 0.3;
          osc.start(ctx.currentTime + i * 0.15); osc.stop(ctx.currentTime + i * 0.15 + 0.12);
        });
      } catch (e) {}
    },
    toggleFullscreen() {
      if (!document.fullscreenElement) { document.documentElement.requestFullscreen().catch(()=>{}); this.isFullscreen = true; }
      else { document.exitFullscreen().catch(()=>{}); this.isFullscreen = false; }
    }
  }
};
</script>

<style scoped>
.kds{background:#0A0F1C;min-height:100vh;color:#E8EDF4;font-family:-apple-system,BlinkMacSystemFont,'Segoe UI',sans-serif}
.kds-header{display:flex;align-items:center;justify-content:space-between;padding:10px 16px;background:#0D1B2A;border-bottom:1px solid #1B2B44;flex-wrap:wrap;gap:8px}
.kds-header-left{display:flex;align-items:center;gap:10px}
.kds-header-mid{display:flex;gap:8px;flex-wrap:wrap}
.kds-header-right{display:flex;gap:6px}
.kds-back{background:0;border:1px solid #2A3F5F;color:#8BA4C4;padding:6px 12px;border-radius:6px;cursor:pointer;font-size:16px}
.kds-back:hover{background:#1B2B44}
.kds-title{margin:0;font-size:18px;font-weight:700;white-space:nowrap}
.kds-live{font-size:11px;font-weight:700;letter-spacing:1px;padding:2px 8px;border-radius:10px}
.kds-live-on{color:#10B981;background:rgba(16,185,129,.15)}
.kds-live-off{color:#EF4444;background:rgba(239,68,68,.15);animation:kds-blink 1s infinite}
@keyframes kds-blink{50%{opacity:.4}}
.kds-select{background:#0A1628;border:1px solid #2A3F5F;color:#E8EDF4;padding:6px 10px;border-radius:6px;font-size:13px}
.kds-icon-btn{background:0;border:1px solid #2A3F5F;color:#E8EDF4;width:36px;height:36px;border-radius:8px;cursor:pointer;font-size:16px;display:flex;align-items:center;justify-content:center}
.kds-icon-btn:hover{background:#1B2B44}

/* Stats */
.kds-stats{display:flex;gap:6px;padding:8px 16px;overflow-x:auto}
.kds-stat{display:flex;flex-direction:column;align-items:center;padding:6px 14px;border-radius:8px;min-width:60px}
.kds-stat-num{font-size:22px;font-weight:800;line-height:1}
.kds-stat-label{font-size:10px;text-transform:uppercase;letter-spacing:.5px;margin-top:2px;opacity:.7}
.kds-stat-red{background:rgba(239,68,68,.12);color:#EF4444}
.kds-stat-orange{background:rgba(245,158,11,.12);color:#F59E0B}
.kds-stat-green{background:rgba(16,185,129,.12);color:#10B981}
.kds-stat-blue{background:rgba(59,130,246,.12);color:#3B82F6}
.kds-stat-muted{background:rgba(139,164,196,.08);color:#8BA4C4}

/* Grid */
.kds-grid{display:grid;grid-template-columns:repeat(auto-fill,minmax(280px,1fr));gap:12px;padding:12px 16px}
.kds-card{background:#0D1B2A;border-radius:12px;padding:14px;border-left:5px solid #2A3F5F;transition:.2s}
.kds-card:hover{transform:translateY(-2px);box-shadow:0 8px 24px rgba(0,0,0,.3)}
.kds-card-new{border-left-color:#EF4444;animation:kds-pulse 2s infinite}
.kds-card-preparing{border-left-color:#F59E0B}
.kds-card-ready{border-left-color:#10B981}
.kds-card-collected{border-left-color:#3B82F6;opacity:.5}
.kds-card-cancelled{border-left-color:#6B7280;opacity:.3}
@keyframes kds-pulse{0%,100%{box-shadow:0 0 0 0 rgba(239,68,68,.3)}50%{box-shadow:0 0 0 8px rgba(239,68,68,0)}}

.kds-card-header{display:flex;align-items:center;gap:8px;margin-bottom:8px}
.kds-order-num{font-size:20px;font-weight:800;font-family:'Courier New',monospace}
.kds-order-badge{font-size:10px;font-weight:700;padding:2px 8px;border-radius:10px;text-transform:uppercase;letter-spacing:.5px}
.kds-badge-new{background:rgba(239,68,68,.2);color:#EF4444}
.kds-badge-preparing{background:rgba(245,158,11,.2);color:#F59E0B}
.kds-badge-ready{background:rgba(16,185,129,.2);color:#10B981}
.kds-badge-collected{background:rgba(59,130,246,.2);color:#3B82F6}
.kds-badge-cancelled{background:rgba(107,114,128,.2);color:#6B7280}
.kds-rush{font-size:11px;font-weight:700;color:#EF4444;animation:kds-blink .5s infinite}
.kds-vip{color:#F59E0B}

.kds-card-customer{font-size:14px;margin-bottom:8px}
.kds-card-customer strong{font-size:15px}
.kds-table{background:#1B2B44;padding:1px 6px;border-radius:4px;font-size:11px;margin-left:6px}

.kds-card-items{border-top:1px solid #1B2B44;padding-top:8px;margin-bottom:8px}
.kds-item{display:flex;align-items:baseline;gap:6px;margin-bottom:4px;font-size:14px}
.kds-item-qty{font-weight:800;color:#F59E0B;min-width:24px}
.kds-item-name{flex:1;font-weight:600}
.kds-item-note{font-size:11px;color:#F59E0B;font-style:italic;display:block;margin-left:30px}

.kds-card-notes{font-size:12px;color:#F59E0B;background:rgba(245,158,11,.08);padding:4px 8px;border-radius:6px;margin-bottom:8px}
.kds-card-meta{display:flex;gap:8px;font-size:11px;color:#8BA4C4;margin-bottom:10px;flex-wrap:wrap}

.kds-card-actions{display:flex;gap:6px;flex-wrap:wrap}
.kds-btn{padding:8px 14px;border:none;border-radius:8px;font-weight:700;font-size:13px;cursor:pointer;transition:.2s}
.kds-btn:hover{filter:brightness(1.15);transform:scale(1.02)}
.kds-btn-orange{background:#F59E0B;color:#000}
.kds-btn-green{background:#10B981;color:#fff}
.kds-btn-blue{background:#3B82F6;color:#fff}
.kds-btn-ghost{background:rgba(255,255,255,.05);color:#8BA4C4;border:1px solid #2A3F5F}

.kds-timer{font-size:13px;color:#F59E0B;font-weight:700;margin-top:6px;font-family:'Courier New',monospace}

.kds-empty{text-align:center;padding:80px 20px;color:#8BA4C4}
.kds-empty-icon{font-size:64px;margin-bottom:16px;opacity:.3}
.kds-muted{color:#4A6A8A;font-size:13px}

/* Settings Panel */
.kds-overlay{position:fixed;inset:0;background:rgba(0,0,0,.7);z-index:100;display:flex;align-items:center;justify-content:center;padding:16px}
.kds-panel{background:#0D1B2A;border-radius:16px;padding:24px;max-width:480px;width:100%;max-height:90vh;overflow-y:auto;position:relative}
.kds-x{position:absolute;top:12px;right:12px;background:rgba(255,255,255,.1);border:none;color:#fff;width:32px;height:32px;border-radius:50%;cursor:pointer;font-size:16px}
.kds-x:hover{background:#EF4444}
.kds-section{margin-top:20px;padding-top:16px;border-top:1px solid #1B2B44}
.kds-section h6{margin:0 0 10px;font-size:14px;color:#4FC3F7}
.kds-station-row{display:flex;justify-content:space-between;align-items:center;padding:6px 0;font-size:14px}
.kds-add-row{display:flex;gap:6px;margin-top:8px}
.kds-inp{background:#0A1628;border:1px solid #2A3F5F;color:#E8EDF4;padding:8px 10px;border-radius:6px;font-size:13px;flex:1}
.kds-inp:focus{border-color:#4FC3F7;outline:none}
.kds-select-sm{background:#0A1628;border:1px solid #2A3F5F;color:#E8EDF4;padding:6px;border-radius:6px;font-size:12px}
.kds-btn-sm{padding:6px 12px;border:none;border-radius:6px;font-size:12px;cursor:pointer;font-weight:600}
.kds-btn-green{background:#10B981;color:#fff}
.kds-toggle{display:flex;align-items:center;gap:10px;padding:6px 0;font-size:14px;cursor:pointer}
.kds-toggle input{width:18px;height:18px;accent-color:#10B981}

/* Fullscreen tweaks */
.kds-fullscreen .kds-grid{grid-template-columns:repeat(auto-fill,minmax(320px,1fr))}
.kds-fullscreen .kds-card{padding:18px}
.kds-fullscreen .kds-order-num{font-size:28px}
.kds-fullscreen .kds-item{font-size:16px}
.kds-fullscreen .kds-btn{padding:12px 20px;font-size:15px}
.kds-fullscreen .kds-stat-num{font-size:28px}

/* Responsive */
@media(max-width:600px){
  .kds-grid{grid-template-columns:1fr}
  .kds-header{flex-direction:column;align-items:stretch}
  .kds-header-mid{flex-direction:column}
}
</style>